import { RunParameter } from "../../../../../api";
import { CaseRunCalculateModel, CaseRunClient, CaseRunCreateModel, CaseRunCreateModelBase, GenerateSampleCaseRunCreateModel, GenerateSampleParameters, LabClient, MaxLrCaseRunCreateModelBase, ParameterTypes, RunParameterModel } from "@app/api2/api";
import { ICreateRenderer } from "../../CaseRunCreate";
import React from "react";
import { ReviewMaxLr } from "../../ReviewMaxLr";
import { Form, FormField } from "@/components/bp/bp-ui";
import { Field } from "formik";
import { FormState, InformationGenerateSample } from "./InformationGenerateSample";
import { MarkerSpecificSettingsEditor } from "../../MarkerSpecificSettingsEditor";
import { RareAllelesFreqSelect } from "@common/RareAllelesFreqSelect";
import { MxPropsEditor } from "./MxPropsEditor";
import { LabKitSelector } from "../../LabKitSelector";
import { GenerateSampleResults } from "../../Details/Results/GenerateSample/GenerateSampleResults";
import { round, sum } from "lodash";
import { Parameters } from "../../Parameters";
import { IParameterRenderer, IParamView, ParameterOwnerModel, RenderParamLabSettingsProps } from "../../ParameterRendererAbstracts";

export class GenerateSampleHandler implements IParameterRenderer, ICreateRenderer<GenerateSampleCaseRunCreateModel, FormState> {

    caseRunClient = new CaseRunClient();
    labClient = new LabClient();

    hasOwnRunList?: () => boolean = () => false;

    save(props: { labId: number, caseId: number, model: CaseRunCreateModelBase }) {
        return this.caseRunClient.createGenerateSample(props.labId, props.caseId, props.model as GenerateSampleCaseRunCreateModel)
    }

    createBlankModel(props: { labId: number, caseId: number, runType: string, evidenceId?: number, ednaTestId?: number }): Promise<GenerateSampleCaseRunCreateModel> {
        return this.caseRunClient.new(props.labId, props.caseId, props.runType, props.evidenceId, props.ednaTestId ?? null) as Promise<GenerateSampleCaseRunCreateModel>
    }

    renderReview(model: CaseRunCreateModelBase, onComplete: (values: any) => void, router: any) {
        return <ReviewMaxLr model={model as MaxLrCaseRunCreateModelBase} onComplete={onComplete} router={router} />
    }

    renderInformation(model: CaseRunCreateModelBase, onComplete: (values: any) => void, router: any, rangeOfContributors: boolean) {
        return <InformationGenerateSample model={model as GenerateSampleCaseRunCreateModel} onComplete={onComplete} />
    }

    runType: string = "gen_sample";

    canHandle(runType: string): boolean {
        return runType == this.runType;
    }

    applyInformationValuesToModel = (model: GenerateSampleCaseRunCreateModel, values: FormState) => {
        model.description = values.description;
        model.hdUnknowns = values.hdUnknowns || 0;
        model.hd = values.hd;
        // model.evidence = values.evidence;
        model.populationId = values.populationId;
        model.numberOfContributors = values.numberOfContributors;

        model.hdKnownRelName = values.hdKnownRelName;
        model.hdKnownRelSampleId = values.hdKnownRelSampleId;

        // set default value for outNoc
        let mix = model.parameters.find(x => x.parameter == "genMixProps");
        if (mix != null) {
            var parsed = JSON.parse(mix.value) as Array<number>;
            // console.log(parsed)
            if (parsed != null && parsed.length != parseInt(values.numberOfContributors)) {
                let noc = parseInt(values.numberOfContributors);
                let vals = [...Array(noc).keys()].map(x => 1);
                // let cnt = sum(vals)

                let restProp = 1;
                vals = vals.map((x, idx) => {
                    let hasNext = (idx != vals.length - 1);

                    let prop = restProp / (hasNext ? 2 : 1);
                    // let prop = restProp / (hasNext ? (idx + 2) : 1);
                    restProp = restProp - prop;
                    return prop;
                })

                mix.value = JSON.stringify(vals);
            }
        }

        let hpUnknowns = model.parameters.find(x => x.parameter == "hpUnknowns");
        if (hpUnknowns != null)
            hpUnknowns.value = model.hpUnknowns.toString();

        let hdUnknowns = model.parameters.find(x => x.parameter == "hdUnknowns");
        if (hdUnknowns != null)
            hdUnknowns.value = model.hdUnknowns.toString();

        let sampleName = model.parameters.find(x => x.parameter == "genEvidenceName");
        if (sampleName != null)
            sampleName.value = `${model.hd.filter(x => x.selected).map(x => x.name).join('_')}_${model.hdUnknowns}uk`;

        return model;
    };

    propsComplete = (model: GenerateSampleCaseRunCreateModel | any): void => {
        model.parameters = model.parameters.sort((a, b) => a.order < b.order ? -1 : 1)
    };

    renderParameterEditor(model: CaseRunCreateModelBase | CaseRunCalculateModel, p: RunParameter, values, onChange, errors, setFieldValue, parameters: Parameters): JSX.Element {
        return p.parameter == "detectionThreshold" ? (<FormField error={!!errors[p.parameter]}>
            <Field name={p.parameter}
                disabled={false} onChange={async (e, v) => { onChange(p, e.target.value); await this.calcDelta(model.labId, e.target.value, parameters); }} />
        </FormField>) : p.parameter == "detectionThresholds" ? (
            <MarkerSpecificSettingsEditor
                readOnly={false}
                onChange={e => { onChange(p, e) }}
                onMarkerValueChange={async (marker, value) => { await this.calcDeltaForMarker(model.labId, value, parameters, marker); }}
                // onMarkersValueChange={async (markers, value) => { await this.calcDeltaForDye(model.labId, value, parameters, markers); }}
                evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
                caseId={model.efmCaseId}
                name={p.parameter}
                labId={model.labId}
                value={values[p.parameter]}
                defaultValue={values.detectionThreshold} />
        ) : p.parameter == "dropInProbabilities" ? (
            <MarkerSpecificSettingsEditor
                readOnly={false}
                onChange={e => { onChange(p, e) }}
                evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
                caseId={model.efmCaseId}
                name={p.parameter}
                labId={model.labId}
                value={values[p.parameter]}
                defaultValue={values.dropInProbability} />
        ) : p.parameter == "dropInHyperParams" ? (<MarkerSpecificSettingsEditor
            readOnly={false}
            onChange={e => { onChange(p, e) }}
            evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
            caseId={model.efmCaseId}
            name={p.parameter}
            labId={model.labId}
            value={values[p.parameter]}
            defaultValue={values.dropInHyperParam} />) :
            p.parameter == "genMixProps" ? <MxPropsEditor labId={model.labId} caseId={model.efmCaseId} value={values[p.parameter]} onChange={(vals) => { onChange(p, vals) }} /> :
                p.parameter == "kitId" ? <LabKitSelector readOnly={false} optionalText="None" kitId={parseInt(p.value)} name={p.parameter} labId={model.labId} onChange={(ev) => onChange(p, ev)} /> :

                    <RareAllelesFreqSelect name={p.parameter} value={values[p.parameter]} onChange={(ev, val) => { onChange(p, val) }} />

    }

    async calcDelta(labId: number, value: any, parameters: Parameters) {
        let res = await this.labClient.calculateDropIn(labId, value);

        await parameters.form.current.setFieldValue("dropInHyperParam", res.lambda);
        await parameters.form.current.setFieldValue("dropInProbability", res.dropInProbability);
    }

    async calcDeltaForMarker(labId: number, value: any, parameters: Parameters, marker: string) {
        let res = await this.labClient.calculateDropIn(labId, value);

        var dropInProps = JSON.parse(parameters.form.current.values.dropInProbabilities)
        var dropInHyps = JSON.parse(parameters.form.current.values.dropInHyperParams)

        dropInProps[marker] = res.dropInProbability
        dropInHyps[marker] = res.lambda

        await parameters.form.current.setFieldValue("dropInHyperParams", JSON.stringify(dropInHyps));
        await parameters.form.current.setFieldValue("dropInProbabilities", JSON.stringify(dropInProps));
    }

    canRenderParameter(parameterName: string): boolean {
        return parameterName == "detectionThreshold"
            || parameterName == "detectionThresholds"
            || parameterName == "dropInHyperParams"
            || parameterName == "dropInProbabilities"
            // || parameterName == "rareAlleleFreqType"
            || parameterName == "genMixProps"
            || parameterName == "kitId"
            ;
    }

    renderParameterView(props: { model: ParameterOwnerModel, p: IParamView }): JSX.Element {
        return props.p.parameter == "kitId" ? <LabKitSelector readOnly={true} kitId={parseInt(props.p.value)} name={props.p.parameter} labId={props.model.labId} /> : props.p.parameter == "genMixProps" ? <MxPropsEditor readonly labId={props.model.labId} caseId={props.model.efmCaseId} value={props.p.value} />
            : props.p.parameterType == ParameterTypes.Json ? <span>
                <MarkerSpecificSettingsEditor
                    readOnly={true}
                    name={props.p.parameter}
                    value={props.p.value}
                    labId={props.model.labId}
                    caseId={props.model.efmCaseId}
                    evidenceId={props.model.evidence.find(x => true)?.id} />

            </span> : props.p.parameter == "rareAlleleFreqType" ?
                <RareAllelesFreqSelect readOnly name={props.p.parameter} value={props.p.value as any} /> : <span>{props.p.value}</span>
    }

    renderParameterLabSettings?(p: RenderParamLabSettingsProps): JSX.Element {
        if (p.parameter == "rareAlleleFreqType") {
            return (
                <RareAllelesFreqSelect readOnly={p.readOnly} name={p.parameter} value={p.value as any} onChange={(ev, a) => { p.onChange(a as any) }} />
            );
        }
        else if (p.parameter == "kitId") {
            return (
                <LabKitSelector optionalText="None" readOnly={p.readOnly} kitId={parseInt(p.value)} name={p.parameter} labId={p.labId} onChange={(a) => { p.onChange(a as any) }} />
            );
        }
        return null;

        // return p.parameter == "rareAlleleFreqType" ? <RareAllelesFreqSelect readOnly={p.readOnly} name={p.parameter} value={p.value as any} onChange={(ev, a) => { p.onChange(a as any) }} /> : null;
    }

    renderResults?: (props: { labId: number, caseId: number, caseRunId: number }) => React.ReactElement = (props) => {
        return <GenerateSampleResults caseId={props.caseId} labId={props.labId} caseRunId={props.caseRunId} />
    };

}

// function parametersToObject<T>(parameters: RunParameterModel[], obj: T) {
//     for (let p of parameters) {
//         obj[p.parameter] = p.value;
//     }

//     return obj;
// }

// function objectToParameters<T>(obj: T, parameters: RunParameterModel[]) {
//     for (let key of Object.keys(obj)) {
//         let param = parameters.find(x => x.parameter == key);
//         if (param)
//             param.value = obj[key]
//     }

//     return parameters;
// }

